import getHTMLAttrs from "@/utils/getHTMLAttrs";
import ReactDOMClient from "react-dom/client";
import type { AppLoaderOptions } from "./types";

/**
 * Creates and initializes a widget loader that handles mounting React components into DOM elements.
 *
 * @param {Object} options - Configuration options for the loader
 * @param {string} [options.initClassName='initialised'] - Class name added to root elements after initialization to prevent re-rendering
 * @param {string} [options.scriptId='hsl-loader'] - ID of the script element used to determine source domain. Must exist in the DOM.
 * @param {string} [options.configAttrPrefix='data-'] - Prefix for HTML data attributes to be collected from root elements
 * @param {string[]} [options.rootSelectors=['hsl-widget']] - Array of CSS selectors to identify root elements for widget mounting
 * @param {boolean} [options.useShadowDOM=false] - When true, creates a Shadow DOM root for component isolation
 * @param {boolean} [options.autoAddCSS=false] - When true, automatically adds CSS to the page
 * @param {Function} [options.beforeRender] - Optional callback executed before rendering, receives ({ dataset, root })
 * @param {Function} options.RootNode - Required function that returns the React node to render, receives ({ dataset, root })
 * @param {Function} [options.preRenderLoopCallback] - Optional callback executed before starting the rendering loop
 * @param {number} [options.retryInterval=0] - Milliseconds between render attempts. Set to 0 to disable retry mechanism
 *
 * @returns {void}
 *
 * @throws {Error} When script element with specified scriptId is not found in the DOM
 * @throws {Error} When rendering fails due to improper configuration or runtime errors
 *
 * @example
 * // Basic usage
 * loader({
 *   scriptId: 'my-widget-loader',
 *   rootSelectors: ['custom-widget'],
 *   RootNode: ({ dataset, root }) => <MyWidget {...dataset} />
 * });
 *
 * @example
 * // Advanced usage with Shadow DOM and preprocessing
 * loader({
 *   scriptId: 'advanced-loader',
 *   rootSelectors: ['.widget', '#special-widget'],
 *   useShadowDOM: true,
 *   configAttrPrefix: 'data-widget-',
 *   beforeRender: ({ dataset, root }) => {
 *     // Perform setup before rendering
 *     root.style.display = 'block';
 *   },
 *   RootNode: ({ dataset, root }) => (
 *     <MyAdvancedWidget
 *       {...dataset}
 *       rootElement={root}
 *     />
 *   ),
 *   retryInterval: 5000 // Retry every 5 seconds
 * });
 *
 * @description
 * The loader function provides a flexible way to mount React components into DOM elements.
 * It follows this process:
 *
 * 1. Waits for DOMContentLoaded event
 * 2. Verifies the presence of the script element with specified scriptId
 * 3. Identifies uninitialized root elements using rootSelectors
 * 4. For each root element:
 *    - Marks it as initialized using initClassName
 *    - Collects data attributes based on configAttrPrefix
 *    - Creates a React root (optionally in Shadow DOM)
 *    - Executes beforeRender callback if provided
 *    - Renders the RootNode
 * 5. If retryInterval > 0, repeats the process periodically
 *
 * The loader is particularly useful for:
 * - Embedding widgets in non-React applications
 * - Progressive enhancement of existing HTML
 * - Micro-frontend implementations
 * - Dynamic widget loading scenarios
 */

export default function reactApploader(options: AppLoaderOptions) {
	const {
		initClassName = "initialised",
		scriptId = "hsl-loader",
		configAttrPrefix = "data-",
		rootSelectors = ["hsl-widget"],
		useShadowDOM = false,
		autoAddCSS = false,
		beforeRender,
		RootNode,
		preRenderLoopCallback,
		retryInterval = 0,
	} = options;
	const getDataset = getHTMLAttrs(configAttrPrefix);

	ready();

	function ready() {
		if (document.readyState !== "loading") {
			onReady();
		} else {
			document.addEventListener("DOMContentLoaded", onReady);
		}
	}

	function onReady() {
		const script = document.getElementById(scriptId) as HTMLScriptElement;
		if (!script) {
			throw new Error(`Unable to find script element with id '${scriptId}'`);
		}
		const cssHref = script.src.replace(/\.js$/, ".css");
		if (retryInterval > 0) {
			setInterval(render.bind(null, { cssHref }), retryInterval);
		} else {
			render({ cssHref });
		}
	}

	function render({ cssHref }: { cssHref: string }) {
		preRenderLoopCallback?.();
		const roots = document.querySelectorAll(
			rootSelectors.map((s) => `${s}:not(.${initClassName})`).join(","),
		);
		if (!roots.length) {
			return;
		}
		for (const root of roots) {
			root.classList.add(initClassName);

			const dataset = getDataset(root);
			const r = ReactDOMClient.createRoot(
				useShadowDOM ? root.attachShadow({ mode: "open" }) : root,
			);

			if (autoAddCSS) {
				const cssRoot = useShadowDOM ? root.shadowRoot : document.head;
				const stylesheetAdded = cssRoot?.querySelector("#hsl-widget-styles");
				!stylesheetAdded &&
					cssRoot?.appendChild(
						Object.assign(document.createElement("link"), {
							rel: "stylesheet",
							href: cssHref,
							id: "hsl-widget-styles",
						}),
					);
			}

			try {
				beforeRender?.({ dataset, root });
				r.render(RootNode({ dataset, root }));
			} catch (err) {
				throw new Error("Error rendering widget", { cause: err });
			}
		}
	}
}
